import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import AppShell from "@/components/AppShell";
import OnboardingTour from "@/components/OnboardingTour";
import { buildPlan, suggestedLetter } from "@/lib/disputePlan";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { NextStepCard } from "@/components/dashboard/NextStepCard";
import { SetupChecklist } from "@/components/dashboard/SetupChecklist";
import { RoundRoadmap } from "@/components/dashboard/RoundRoadmap";
import { LetterStatusSection } from "@/components/dashboard/LetterStatusSection";
import { RecentReportCard } from "@/components/dashboard/RecentReportCard";

const safe = (p, fallback) => p.then((r) => r.data).catch(() => fallback);

function SuggestedLetter({ plan }) {
  const [card, setCard] = useState(null);
  const s = suggestedLetter(plan.rounds);
  useEffect(() => {
    if (!plan.latestReport) return;
    api.get("/letter-library").then((r) => setCard(r.data.cards.find((c) => c.round === s.round && c.status === "available") || null)).catch(() => {});
  }, [plan.latestReport, s.round]);
  if (!plan.latestReport) return null;
  return (
    <Link to="/app/letters" className="mt-3 flex items-center gap-3 min-h-[56px] rounded-2xl border border-purple-200 bg-white px-4 py-3 hover:border-purple-400 transition-colors" data-testid="dashboard-suggested-letter">
      <BookOpen size={20} className="text-purple-600 shrink-0" />
      <span className="flex-1 text-sm text-slate-700">
        {card ? <>Suggested Round {s.round} letter: <b className="text-slate-900">{card.name}</b>. </> : null}
        Open the Letter Library to see the recommended letter for each account, plus special requests.
      </span>
    </Link>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    Promise.all([
      safe(api.get("/reports"), []), safe(api.get("/documents"), []), safe(api.get("/identity-documents"), null),
      safe(api.get("/mailings"), []), safe(api.get("/cfpb/drafts"), []), safe(api.get("/me"), null),
    ]).then(([reports, docs, identity, mailings, cfpbDrafts, me]) => {
      setData({ reports, docs, identity, mailings, cfpbDrafts });
      if (me && me.has_completed_onboarding !== true) setShowTour(true);
    });
  }, []);

  const plan = useMemo(() => (data ? buildPlan(data) : null), [data]);

  const go = (to) => {
    if (to.startsWith("#")) { document.getElementById(to.slice(1))?.scrollIntoView({ behavior: "smooth" }); return; }
    nav(to);
  };

  return (
    <AppShell>
      {showTour && <OnboardingTour onDismiss={() => setShowTour(false)} />}
      <header>
        <div className="eyebrow">Dashboard</div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 mt-1" data-testid="dashboard-heading">Your Dispute Action Plan</h1>
        <p className="mt-2 text-sm sm:text-base text-slate-600">Complete each step in order. We'll show you exactly what to do next.</p>
      </header>
      {!plan ? (
        <div className="mt-6 h-48 rounded-3xl bg-slate-100 animate-pulse" data-testid="dashboard-loading" />
      ) : (
        <>
          <NextStepCard next={plan.next} onGo={go} />
          <SuggestedLetter plan={plan} />
          <SetupChecklist items={plan.setup} />
          <RoundRoadmap rounds={plan.rounds} currentRound={plan.currentRound} cfpbUnlocked={plan.cfpbUnlocked} cfpbCount={plan.cfpbCount} onCfpb={() => nav("/app/cfpb")} />
          <LetterStatusSection items={plan.items} />
          <RecentReportCard report={plan.latestReport} onOpen={(id) => nav(`/app/review/${id}`)} />
        </>
      )}
    </AppShell>
  );
}
